import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTenant } from '../Context/TenantContext';
import API_BASE_URL from '../Config/config';

import { useCollegeLogo } from '../hooks/useCollegeLogo';

/**
 * CollegeHeader
 *
 * Global enterprise header for all College Tenant screens.
 * Single source of truth for institution branding - logo, college name, role badge.
 * Sticky at top, spans full viewport width.
 *
 * Usage: <CollegeHeader /> - no props required.
 */
const CollegeHeader = () => {
  const { getCollegeName, tenant } = useTenant();
  const { logo } = useCollegeLogo();
  const [apiName, setApiName]   = useState('');

  useEffect(() => {
    const fetchCollegeData = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const username = user?.username || user?.userId;
        if (!username) return;
        const res = await axios.get(`${API_BASE_URL}/colleges/username/${username}`);
        if (res.data?.name) setApiName(res.data.name);
      } catch { /* silent - tenant context values used as fallback */ }
    };
    fetchCollegeData();
  }, []);

  /* ── Resolved values (priority: API > tenant context > defaults) ── */
  const ctxName  = getCollegeName?.() || '';

  const displayName = apiName  || tenant?.collegeName || tenant?.college_name || ctxName || 'Institution Portal';

  const storedUser  = JSON.parse(localStorage.getItem('user') || '{}');
  const adminName   = storedUser?.username || storedUser?.name || 'Admin';
  const roleLabel   = storedUser?.role || 'Admin';
  const initials    = adminName.charAt(0).toUpperCase();

  return (
    <header
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        height: '61px',
        flexShrink: 0,
        background: '#FFFFFF',
        borderBottom: '1px solid var(--border-subtle)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 28px',
      }}
    >
      {/* ── Left: logo + institution name + Portal Label ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
        <img
          src={logo}
          alt={displayName}
          onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/logo192.png'; }}
          style={{
            width: '34px',
            height: '34px',
            objectFit: 'contain',
            flexShrink: 0,
            border: '1px solid var(--border-subtle, #E5E7EB)',
            borderRadius: '8px',
            padding: '2px',
          }}
        />
        <div style={{ minWidth: 0 }}>
          <h1
            style={{
              fontSize: '0.875rem',
              fontWeight: 800,
              color: '#843FEF',
              letterSpacing: '-0.01em',
              lineHeight: 1,
              margin: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 'clamp(160px, 30vw, 360px)',
            }}
          >
            {displayName}
          </h1>
          <p
            style={{
              fontSize: '0.625rem',
              color: 'var(--text-muted, #6B7280)',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.09em',
              marginTop: '3px',
              lineHeight: 1,
            }}
          >
            Admin Portal &middot; The Skill Genie
          </p>
        </div>
      </div>

      {/* ── Right: user identity pill ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '5px 12px 5px 6px',
          borderRadius: '999px',
          background: 'rgba(132, 63, 239, 0.05)',
          border: '1px solid rgba(132, 63, 239, 0.14)',
          flexShrink: 0,
        }}
      >
        {/* Avatar initials fallback */}
        <div
          style={{
            width: '26px',
            height: '26px',
            borderRadius: '50%',
            background: '#843FEF',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.6875rem',
            fontWeight: 800,
            flexShrink: 0,
            letterSpacing: 0,
          }}
        >
          {initials}
        </div>
        {/* Profile Name */}
        <span
          style={{
            fontSize: '0.75rem',
            fontWeight: 700,
            color: '#843FEF',
            letterSpacing: '0.01em',
            maxWidth: '140px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {adminName}
        </span>
        {/* Role label */}
        <span
          style={{
            fontSize: '0.6rem',
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: '0.07em',
            color: 'rgba(132, 63, 239, 0.65)',
            borderLeft: '1px solid rgba(132, 63, 239, 0.2)',
            paddingLeft: '8px',
            lineHeight: 1,
          }}
        >
          {roleLabel}
        </span>
      </div>
    </header>
  );
};

export default CollegeHeader;